import { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useSEO, generateBreadcrumbSchema } from '../../../utils/seo';
import { directoryBusinesses } from '../../../mocks/directory';
import { getBusinessDetail } from '../../../mocks/directoryDetails';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import BusinessHero from './components/BusinessHero';
import QuickActions from './components/QuickActions';
import AboutSection from './components/AboutSection';
import HoursCard from './components/HoursCard';
import PhotoGallery from './components/PhotoGallery';
import ServicesSection from './components/ServicesSection';
import ReviewsSection from './components/ReviewsSection';
import ContactSidebar from './components/ContactSidebar';
import InquiryForm from './components/InquiryForm';
import SimilarBusinesses from './components/SimilarBusinesses';
import FavoriteButton from '../../../components/FavoriteButton';

import BasicReviewsSection from './components/BasicReviewsSection';

const FORM_URL = 'https://readdy.ai/api/form/d6aaoima728k8ctu3740';

// Helper function to determine LocalBusiness schema type based on category
const getSchemaType = (category: string): string => {
  const schemaMap: Record<string, string> = {
    health: 'Dentist',
    home: 'Plumber',
    beauty: 'HairSalon',
    auto: 'AutoRepair',
    restaurant: 'Restaurant',
    retail: 'Store',
    professional: 'ProfessionalService',
    education: 'EducationalOrganization'
  };
  return schemaMap[category] || 'LocalBusiness';
};

export default function DirectoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numId = Number(id);
  const [shareToast, setShareToast] = useState(false);

  // Try detailed data first, fall back to basic directory listing
  const detail = getBusinessDetail(numId);
  const basicBiz = directoryBusinesses.find((b) => b.id === numId);

  // Generate schemas with category-specific @type
  const schemas = detail ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': getSchemaType(detail.category),
        name: detail.name,
        description: detail.longDescription,
        image: detail.image,
        logo: detail.logo,
        address: {
          '@type': 'PostalAddress',
          streetAddress: detail.address.split(',')[0],
          addressLocality: 'Roseville',
          addressRegion: 'CA',
          postalCode: '95678',
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 38.7521,
          longitude: -121.2880,
        },
        telephone: detail.phone,
        email: detail.email,
        url: detail.website,
        priceRange: detail.priceRange,
        openingHoursSpecification: detail.weeklyHours.map((day) => ({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: day.day,
          opens: day.hours.split(' - ')[0],
          closes: day.hours.split(' - ')[1] || '17:00',
        })),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: detail.rating,
          reviewCount: detail.reviews,
          bestRating: '5',
          worstRating: '1',
        },
      },
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Directory', url: '/directory' },
        { name: detail.name, url: `/directory/${id}` }
      ])
    ]
  } : basicBiz ? {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': getSchemaType(basicBiz.category),
        name: basicBiz.name,
        description: basicBiz.description,
        image: basicBiz.image,
        address: {
          '@type': 'PostalAddress',
          streetAddress: basicBiz.address.split(',')[0],
          addressLocality: 'Roseville',
          addressRegion: 'CA',
          postalCode: '95678',
          addressCountry: 'US',
        },
        telephone: basicBiz.phone,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: basicBiz.rating,
          reviewCount: basicBiz.reviews,
        },
      },
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Directory', url: '/directory' },
        { name: basicBiz.name, url: `/directory/${id}` }
      ])
    ]
  } : undefined;

  useSEO({
    title: detail
      ? `${detail.name} — ${detail.categoryLabel} | Roseville Business Directory`
      : basicBiz
      ? `${basicBiz.name} | Roseville Business Directory`
      : 'Business Not Found | Roseville.org',
    description: detail
      ? detail.longDescription.slice(0, 155)
      : basicBiz
      ? basicBiz.description
      : 'This business listing could not be found.',
    keywords: detail
      ? `${detail.name}, ${detail.categoryLabel}, Roseville business, Roseville CA, ${detail.category}, local business directory`
      : basicBiz
      ? `${basicBiz.name}, Roseville business, ${basicBiz.category}`
      : 'Roseville business directory',
    canonical: `/directory/${id}`,
    ogType: 'business.business',
    ogImage: detail?.image || basicBiz?.image,
    ogImageAlt: detail ? `${detail.name} - ${detail.categoryLabel}` : basicBiz ? basicBiz.name : undefined,
    schema: schemas,
  });

  if (!detail && !basicBiz) {
    return <Navigate to="/directory" replace />;
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: detail?.name || basicBiz?.name,
          url: window.location.href,
        })
        .catch(() => {
          /* silently ignore */
        });
    } else {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          setShareToast(true);
          setTimeout(() => setShareToast(false), 2500);
        })
        .catch(() => {
          /* silently ignore */
        });
    }
  };

  // If we have full detail data, render the rich page
  if (detail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20">
          <BusinessHero
            name={detail.name}
            tagline={detail.tagline}
            categoryLabel={detail.categoryLabel}
            image={detail.image}
            logo={detail.logo}
            rating={detail.rating}
            reviews={detail.reviews}
            priceRange={detail.priceRange}
            verified={detail.verified}
            featured={detail.featured}
            offer={detail.offer}
            breadcrumbs={[
              { label: 'Home', to: '/' },
              { label: 'Directory', to: '/directory' },
              { label: detail.name },
            ]}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Quick Actions Bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <FavoriteButton
                    itemType="business"
                    itemId={detail.id}
                    itemTitle={detail.name}
                    itemImage={detail.image}
                    itemData={{ category: detail.category, rating: detail.rating }}
                    showLabel={true}
                    className="hover:text-red-500"
                  />
                  <QuickActions
                    phone={detail.phone}
                    address={detail.address}
                    name={detail.name}
                    website={detail.website}
                    onShare={handleShare}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main content */}
              <div className="lg:col-span-2 space-y-8">
                <AboutSection
                  longDescription={detail.longDescription}
                  yearEstablished={detail.yearEstablished}
                  attributes={detail.attributes}
                />
                <PhotoGallery photos={detail.photos} businessName={detail.name} />
                <ServicesSection services={detail.services} />
                <ReviewsSection
                  rating={detail.rating}
                  totalReviews={detail.reviews}
                  reviewsList={detail.reviewsList}
                  businessName={detail.name}
                />
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <ContactSidebar
                  phone={detail.phone}
                  email={detail.email}
                  website={detail.website}
                  address={detail.address}
                  name={detail.name}
                  socialMedia={detail.socialMedia}
                />
                <HoursCard hours={detail.weeklyHours} />
                <InquiryForm businessName={detail.name} formUrl={FORM_URL} />

                {/* Advertise CTA */}
                <div className="bg-gradient-to-br from-teal-700 to-teal-800 rounded-2xl p-6 text-white">
                  <h3 className="text-base font-bold mb-2">Own a Local Business?</h3>
                  <p className="text-sm text-white/80 mb-4 leading-relaxed">
                    Get your business listed on Roseville.org and reach thousands of local residents.
                  </p>
                  <Link
                    to="/submit-business"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-teal-700 text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line text-sm"></i>
                    Submit Your Business
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <SimilarBusinesses currentId={detail.id} category={detail.category} />
        </div>
        <Footer />

        {/* Share toast */}
        {shareToast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-gray-900 text-white text-sm font-medium rounded-xl shadow-lg animate-fade-in">
            <i className="ri-check-line mr-2"></i> Link copied to clipboard
          </div>
        )}
      </div>
    );
  }

  // Fallback: basic listing view for businesses without full detail data
  const biz = basicBiz!;
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-20">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-500 hover:text-teal-600 cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400 text-xs"></i>
              <Link to="/directory" className="text-gray-500 hover:text-teal-600 cursor-pointer">
                Directory
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400 text-xs"></i>
              <span className="text-gray-900 font-medium">{biz.name}</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="relative h-[340px] overflow-hidden">
          <img
            src={biz.image}
            alt={biz.name}
            title={`${biz.name} Roseville CA`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-end gap-4">
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <i className={`${biz.logo} text-teal-600 text-xl`}></i>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">{biz.name}</h1>
                  <p className="text-white/80 text-sm italic">{biz.tagline}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">About</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{biz.description}</p>
              </div>
              {biz.offer && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-coupon-3-line text-amber-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-800">Current Offer</p>
                    <p className="text-sm text-amber-700">{biz.offer}</p>
                  </div>
                </div>
              )}

              <BasicReviewsSection
                rating={biz.rating}
                totalReviews={biz.reviews}
                businessName={biz.name}
              />
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 mb-4">Business Info</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-semibold text-gray-900">{biz.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <a
                        href={`tel:${biz.phone}`}
                        className="text-sm font-semibold text-gray-900 hover:text-teal-600 cursor-pointer"
                      >
                        {biz.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Hours</p>
                      <p className="text-sm font-semibold text-gray-900">{biz.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-gray-100 space-y-2.5">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      biz.name + ' ' + biz.address
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="block w-full bg-teal-600 text-white text-center py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-navigation-line mr-2"></i> Get Directions
                  </a>
                  <a
                    href={`tel:${biz.phone}`}
                    className="block w-full bg-gray-100 text-gray-900 text-center py-3 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i> Call Business
                  </a>
                </div>
              </div>
              <InquiryForm businessName={biz.name} formUrl={FORM_URL} />
            </div>
          </div>
        </div>

        <SimilarBusinesses currentId={biz.id} category={biz.category} />
      </div>
      <Footer />
    </div>
  );
}